import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, Loader2, Upload, ArrowLeft, ArrowRight, Info } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { authApi } from "../../lib/auth";
import { errorMessage } from "../../lib/api";
import { USER_FIELDS, LICENSE_FIELDS, detectMapping, parseCsvFile, toImportRows, toImportLicenses, distinctProducts } from "../../lib/csvImport";
import CsvMappingTable from "./CsvMappingTable";
import CsvProductTable from "./CsvProductTable";

const CHUNK = 1000;

/** Import Microsoft 365 admin center exports as a scan (new "CSV" tenant or an existing tenant). */
export default function ImportCsvDialog({ open, onOpenChange, tenants = [], tenant = null, onDone }) {
  const [step, setStep] = useState(1);
  const [target, setTarget] = useState(tenant ? "existing" : "new");
  const [tenantId, setTenantId] = useState(tenant?.id || "");
  const [newName, setNewName] = useState("");
  const [users, setUsers] = useState(null);
  const [licenses, setLicenses] = useState(null);
  const [userMap, setUserMap] = useState({});
  const [licMap, setLicMap] = useState({});
  const [products, setProducts] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);

  const rows = useMemo(() => (users ? toImportRows(users.rows, userMap) : []), [users, userMap]);
  const licRows = useMemo(() => (licenses ? toImportLicenses(licenses.rows, licMap) : []), [licenses, licMap]);
  const names = useMemo(() => distinctProducts(rows, licRows), [rows, licRows]);
  const counts = useMemo(() => rows.reduce((acc, r) => (r.licenses.forEach((l) => (acc[l] = (acc[l] || 0) + 1)), acc), {}), [rows]);

  useEffect(() => {
    if (step !== 2) return undefined;
    if (!names.length) return setProducts([]);
    setProducts(null);
    authApi.imports
      .resolveProducts(names)
      .then(setProducts)
      .catch((err) => toast.error("Could not match products", { description: errorMessage(err) }));
    return undefined;
  }, [step, names]);

  const pick = (kind) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const parsed = await parseCsvFile(file);
      if (!parsed.headers.length) return toast.error("No header row found in this CSV");
      if (kind === "users") {
        setUsers({ ...parsed, name: file.name });
        setUserMap(detectMapping(parsed.headers, USER_FIELDS));
      } else {
        setLicenses({ ...parsed, name: file.name });
        setLicMap(detectMapping(parsed.headers, LICENSE_FIELDS));
      }
    } catch (err) {
      toast.error("Could not read the file", { description: err?.message });
    }
    return undefined;
  };

  const canReview = (users || licenses) && (target === "existing" ? tenantId : newName.trim().length > 0);
  const canImport = canReview && (!users || userMap.upn) && (!licenses || licMap.product) && (rows.length || licRows.length) && products;

  const run = async () => {
    setBusy(true);
    setProgress(0);
    let session = null;
    try {
      session = await authApi.imports.create({ tenant_id: target === "existing" ? tenantId : null, new_tenant_name: target === "new" ? newName.trim() : null, licenses: licRows, file_name: users?.name || licenses?.name || "" });
      for (let i = 0; i < rows.length; i += CHUNK) {
        await authApi.imports.addRows(session.id, rows.slice(i, i + CHUNK));
        setProgress(Math.round(((i + CHUNK) / Math.max(rows.length, 1)) * 90));
      }
      const res = await authApi.imports.commit(session.id);
      setProgress(100);
      toast.success(`Imported ${res.users.toLocaleString()} users and ${res.skus} SKUs`, { description: res.unknown_products.length ? `${res.unknown_products.length} product name(s) were not recognized.` : undefined });
      onOpenChange(false);
      onDone?.(res);
    } catch (err) {
      toast.error("Import failed", { description: errorMessage(err) });
      if (session) authApi.imports.cancel(session.id).catch(() => {});
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="sm:max-w-3xl tenant-dialog" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle>
            <FileSpreadsheet size={16} /> Import a Microsoft 365 export
          </DialogTitle>
          <DialogDescription>
            Microsoft 365 admin center → <b>Users › Active users › Export users</b> (users + their licenses) and optionally <b>Billing › Licenses › Export</b> (purchased seats). No Microsoft connection needed - gap analysis stays unavailable without Graph.
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="ws-form" data-testid="import-step-files">
            <div className="import-target">
              <label className={`radio-card ${target === "new" ? "on" : ""}`} data-testid="import-target-new">
                <input type="radio" name="target" checked={target === "new"} onChange={() => setTarget("new")} />
                <span>
                  <b>Create a new tenant</b>
                  <small>Type “Imported (CSV)” - re-import later to add a new scan and compare.</small>
                </span>
              </label>
              <label className={`radio-card ${target === "existing" ? "on" : ""}`} data-testid="import-target-existing">
                <input type="radio" name="target" checked={target === "existing"} onChange={() => setTarget("existing")} disabled={!tenants.length && !tenant} />
                <span>
                  <b>Add as a scan to an existing tenant</b>
                  <small>Attach the export to a tenant you already track.</small>
                </span>
              </label>
            </div>
            {target === "new" ? (
              <label className="auth-field">
                <span>Tenant display name</span>
                <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Fabrikam (CSV)" data-testid="import-new-name" />
              </label>
            ) : (
              <label className="auth-field">
                <span>Tenant</span>
                <select className="ws-native-select" value={tenantId} onChange={(e) => setTenantId(e.target.value)} data-testid="import-tenant-select">
                  <option value="">Choose a tenant…</option>
                  {(tenant ? [tenant] : tenants).map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <div className="file-grid">
              <label className={`file-drop ${users ? "on" : ""}`} data-testid="import-users-file">
                <input type="file" accept=".csv,text/csv" onChange={pick("users")} />
                <Upload size={16} />
                <b>Users export (.csv)</b>
                <small>{users ? `${users.name} · ${users.rows.length.toLocaleString()} rows` : "User principal name, Display name, Licenses, Block credential…"}</small>
              </label>
              <label className={`file-drop ${licenses ? "on" : ""}`} data-testid="import-licenses-file">
                <input type="file" accept=".csv,text/csv" onChange={pick("licenses")} />
                <Upload size={16} />
                <b>Licenses export (.csv) - optional</b>
                <small>{licenses ? `${licenses.name} · ${licenses.rows.length.toLocaleString()} rows` : "Product, total and assigned seats"}</small>
              </label>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="ws-form" data-testid="import-step-review">
            {users && (
              <details open className="import-section">
                <summary>
                  Users columns <span className="muted small">{rows.length.toLocaleString()} users · {rows.filter((r) => r.licenses.length).length.toLocaleString()} licensed</span>
                </summary>
                <CsvMappingTable fields={USER_FIELDS} headers={users.headers} mapping={userMap} sample={users.rows[0]} onChange={setUserMap} testId="map-users" />
              </details>
            )}
            {licenses && (
              <details open className="import-section">
                <summary>
                  Licenses columns <span className="muted small">{licRows.length} products</span>
                </summary>
                <CsvMappingTable fields={LICENSE_FIELDS} headers={licenses.headers} mapping={licMap} sample={licenses.rows[0]} onChange={setLicMap} testId="map-licenses" />
              </details>
            )}
            <CsvProductTable products={products} counts={counts} />
            {!rows.length && !licRows.length && (
              <div className="ws-alert warn">
                <Info size={13} /> No rows could be read - check the “User principal name” / “Product” mapping.
              </div>
            )}
            {busy && (
              <div className="import-progress" data-testid="import-progress">
                <span style={{ width: `${progress}%` }} />
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {step === 2 && (
            <Button type="button" variant="outline" onClick={() => setStep(1)} disabled={busy} data-testid="import-back">
              <ArrowLeft size={14} /> Back
            </Button>
          )}
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          {step === 1 ? (
            <Button type="button" onClick={() => setStep(2)} disabled={!canReview} data-testid="import-next">
              Review <ArrowRight size={14} />
            </Button>
          ) : (
            <Button type="button" onClick={run} disabled={!canImport || busy} data-testid="import-run">
              {busy ? <Loader2 size={14} className="spin" /> : <Upload size={14} />} Import {rows.length ? `${rows.length.toLocaleString()} users` : `${licRows.length} products`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
